import React from 'react';
import { Download, ArrowRight, RotateCcw, ArrowLeft, CheckCircle2, Lock, AlertTriangle } from 'lucide-react';
import { ECOSYSTEM_APPS } from '../constants';
import { trackEvent } from '../services/analytics';

const ecosystemColors: Record<string, string> = {
  blue: 'border-blue-500/30 bg-blue-900/10 hover:bg-blue-900/20 text-blue-300',
  violet: 'border-violet-500/30 bg-violet-900/10 hover:bg-violet-900/20 text-violet-300',
  amber: 'border-amber-500/30 bg-amber-900/10 hover:bg-amber-900/20 text-amber-300',
};

interface DownloadViewProps {
  imageCount: number;
  onDownload: () => void;
  onStartNew: () => void;
  onBackToEditor: () => void;
  isAuthenticated: boolean;
  skippedFiles?: { fileName: string; reason: string }[];
}

const DownloadView: React.FC<DownloadViewProps> = ({
  imageCount,
  onDownload,
  onStartNew,
  onBackToEditor,
  isAuthenticated,
  skippedFiles = [],
}) => {
  const processedCount = imageCount - skippedFiles.length;
  return (
    <div className="flex-1 container mx-auto px-4 py-12 sm:py-16 max-w-4xl">
      <div className="text-center animate-fade-in">
        {/* Success Icon */}
        <div className="w-20 h-20 mx-auto mb-6 bg-cyan-900/30 border border-cyan-500/30 rounded-full flex items-center justify-center">
          <CheckCircle2 size={40} className="text-cyan-400" />
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-2">Your images are ready</h2>
        <p className="text-slate-400 mb-8">
          {processedCount} image{processedCount !== 1 ? 's' : ''} processed and packaged into a ZIP file.
        </p>

        {skippedFiles.length > 0 && (
          <div className="mb-8 mx-auto max-w-xl text-left p-4 rounded-xl bg-amber-900/15 border border-amber-500/30">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold mb-2">
              <AlertTriangle size={16} />
              {skippedFiles.length} file{skippedFiles.length !== 1 ? 's' : ''} skipped
            </div>
            <ul className="text-xs text-amber-200/80 space-y-1 max-h-32 overflow-y-auto">
              {skippedFiles.map((s) => (
                <li key={s.fileName} className="flex justify-between gap-3">
                  <span className="truncate">{s.fileName}</span>
                  <span className="text-amber-300/70 shrink-0">{s.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Download Button */}
        <button
          onClick={onDownload}
          className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] touch-manipulation mb-3"
        >
          <Download size={22} />
          Download ZIP
        </button>

        {!isAuthenticated && (
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5 mb-8">
            <Lock size={12} />
            Free sign-in required to download
          </p>
        )}

        {/* Action Links */}
        <div className="flex items-center justify-center gap-4 mt-6 mb-16">
          <button
            onClick={onBackToEditor}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 transition-all"
          >
            <ArrowLeft size={16} />
            Back to Editor
          </button>
          <button
            onClick={onStartNew}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 transition-all"
          >
            <RotateCcw size={16} />
            Start New Batch
          </button>
        </div>

        {/* Ecosystem Upsell */}
        <div className="border-t border-slate-800 pt-12">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Explore more ReHub tools</p>
          <h3 className="text-lg font-semibold text-slate-200 mb-6">Take your property marketing further</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ECOSYSTEM_APPS.map((app) => {
              const colors = ecosystemColors[app.color] || ecosystemColors.blue;
              return (
                <a
                  key={app.name}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('batch_upsell_clicked', null, { target: app.name })}
                  className={`flex flex-col items-center gap-3 p-5 rounded-2xl border transition-all group ${colors}`}
                >
                  <div className="h-14 flex items-center justify-center">
                    <img src={app.logo} alt={app.name} className={`max-h-full ${(app as any).logoWidth || 'w-[140px]'} object-contain`} />
                  </div>
                  <p className="text-xs text-slate-400">{app.tagline}</p>
                  <span className="flex items-center gap-1 text-xs font-medium group-hover:gap-2 transition-all">
                    Try it <ArrowRight size={12} />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadView;
